import React from 'react';
import {useComponent} from '../component-library';
import {GraphicRadioButton} from './graphic-radio-button';
import {GraphicRadioButtonRow} from './graphic-radio-button-row';
import {useFeatureAppId} from '../utils/use-feature-app-id';
import {
  ChargerTypeValues,
  ChargeScenarioOptions,
} from '@volkswagen-onehub/whitelabel-e-mobility-data-types';
import {generateTestId, TestIdElementType} from '../utils/test';
import {IconName} from '../types';

export interface ChargingTypeSelectionProps {
  value: ChargerTypeValues | '';
  chargerTypeHousehold: string;
  chargerTypeWallbox: string;
  chargerTypeStationDC: string;
  onChange: (newValue: ChargerTypeValues) => void;
  name: string;
  questionTextId: string;
  chargingLocation: ChargeScenarioOptions;
}

export const ChargerTypeSelection: React.FC<ChargingTypeSelectionProps> =
  React.memo(
    ({
      value,
      chargerTypeHousehold,
      chargerTypeWallbox,
      chargerTypeStationDC,
      onChange,
      name,
      questionTextId,
      chargingLocation,
    }: ChargingTypeSelectionProps) => {
      const featureAppId = useFeatureAppId();
      const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        e.stopPropagation();

        onChange(e.target.value as ChargerTypeValues);
      };

      const Icon = useComponent('Icon');

      const chargerTypeOptions = Object.keys(ChargerTypeValues);

      const chargerTypeOptionToIconName: Record<string, IconName> = {
        socketAC: 'ChargingSocket',
        wallboxAC: 'ChargingWallbox',
        default: 'ChargingStation',
      };

      const chargerTypeOptionToContent: Record<string, string> = {
        socketAC: chargerTypeHousehold,
        wallboxAC: chargerTypeWallbox,
        default: chargerTypeStationDC,
      };

      return (
        <GraphicRadioButtonRow>
          {chargerTypeOptions.map((chargerTypeOption) => {
            const iconName =
              chargerTypeOptionToIconName[chargerTypeOption] ||
              chargerTypeOptionToIconName.default;
            const content =
              chargerTypeOptionToContent[chargerTypeOption] ||
              chargerTypeOptionToContent.default;

            return (
              <GraphicRadioButton
                key={chargerTypeOption}
                value={chargerTypeOption}
                iconMobile={<Icon name={iconName} size="small" ariaHidden />}
                iconDesktop={<Icon name={iconName} size="medium" ariaHidden />}
                content={content}
                selected={value === chargerTypeOption}
                onChange={handleChange}
                name={name}
                questionTextId={questionTextId}
                disabled={
                  !(chargerTypeOption in chargingLocation) ||
                  chargingLocation[chargerTypeOption as ChargerTypeValues]
                    ?.length === 0
                }
                testId={generateTestId({
                  featureAppId,
                  elementName: `charger-type-${chargerTypeOption.toLowerCase()}`,
                  elementType: TestIdElementType.button,
                })}
              />
            );
          })}
        </GraphicRadioButtonRow>
      );
    }
  );

ChargerTypeSelection.displayName = 'ChargerTypeSelection';
